<template>
  <div class="cashback-regra-faixas">
    <div class="cashback-regra-faixas__section">
      <div class="cashback-regra__secao-titulo">Base de cálculo</div>
      <CRow>
        <CCol md="8">
          <select class="form-control" :value="item.baseValor" @change="alterar('baseValor', $event.target.value)">
            <option v-for="base in basesValor" :key="base.chave" :value="base.chave">{{ base.label }}</option>
          </select>
          <small class="cashback-regra-faixas__base-dica">
            O valor dos itens já vem com os descontos e acréscimos do plano de pagamento (ex.: pagamento antecipado), promoções, negociação e
            embalagem. Cupom, valor do frete, taxa fixa e taxa do cartão não entram.
          </small>
        </CCol>
      </CRow>
    </div>

    <div class="cashback-regra-faixas__section">
      <div class="cashback-regra__secao-titulo">Faixas por valor do pedido</div>
      <div class="cashback-regra__aviso">
        <fa-icon icon="info-circle" class="mr-2" />
        O percentual da faixa em que o valor da base se encaixa é aplicado sobre a base inteira. Cada faixa começa depois do "Até" da anterior; ao alterar um "Até", as faixas seguintes
        coladas a ela andam juntas, mantendo o tamanho. Se houver intervalo entre faixas, ou faixa com 0%, pedidos com a base nesse valor não geram cashback. Só a
        última pode ficar sem teto ("Até" zerado).
      </div>

      <div class="cashback-regra-faixas__cabecalho">
        <span>A partir de</span>
        <span>Até <small class="cashback-regra-faixas__cabecalho-dica">(0 = sem teto)</small></span>
        <span>Retorno</span>
        <span></span>
      </div>

      <template v-for="(faixa, indice) in item.faixas">
        <div :key="`linha-${indice}-${remontagem}-${versoes[indice] || 0}`" class="cashback-regra-faixas__linha">
          <InputMoney
            :value="faixa.valorMinimo"
            :class="{ 'cashback-regra-faixas__entrada--erro': erros[indice].valorMinimo }"
            @focus.native="focar(indice, 'valorMinimo')"
            @blur.native="desfocar"
            @input="definirValor(indice, 'valorMinimo', $event)"
          />
          <InputMoney
            :value="faixa.valorMaximo"
            :class="{ 'cashback-regra-faixas__entrada--erro': erros[indice].valorMaximo }"
            @focus.native="focar(indice, 'valorMaximo')"
            @blur.native="desfocar"
            @input="definirValor(indice, 'valorMaximo', $event)"
          />
          <InputPercent
            :value="faixa.percentual"
            :class="{ 'cashback-regra-faixas__entrada--erro': erros[indice].percentual }"
            @input="definirPercentual(indice, $event)"
          />
          <button
            type="button"
            class="cashback-regra-faixas__remover"
            title="Remover faixa"
            :aria-label="`Remover faixa ${indice + 1}`"
            @click="removerFaixa(indice)"
          >
            <fa-icon icon="trash-alt" />
          </button>
        </div>
        <div v-if="Object.keys(erros[indice]).length" :key="`erro-${indice}`" class="cashback-regra-faixas__erros">
          <span>{{ erros[indice].valorMinimo }}</span>
          <span>{{ erros[indice].valorMaximo }}</span>
          <span>{{ erros[indice].percentual }}</span>
        </div>
        <div v-if="avisos[indice]" :key="`aviso-${indice}`" class="cashback-regra-faixas__aviso-linha">
          <fa-icon icon="exclamation-triangle" class="mr-1" />{{ avisos[indice] }}
        </div>
      </template>

      <div v-if="!item.faixas.length" class="cashback-regra-faixas__vazio">Nenhuma faixa cadastrada.</div>

      <CButton size="sm" color="primary" variant="outline" class="mt-2" @click="adicionarFaixa">
        <fa-icon icon="plus" class="mr-1" /> Adicionar faixa
      </CButton>

      <div v-if="tetoUltimaFaixa" class="cashback-regra-teto" role="radiogroup" aria-labelledby="cashback-regra-teto-titulo">
        <div id="cashback-regra-teto-titulo" class="cashback-regra__rotulo">
          Pedidos acima de {{ formatMoney(tetoUltimaFaixa) }}, o fim da última faixa
        </div>
        <label
          v-for="opcao in opcoesTeto"
          :key="opcao.chave"
          class="cashback-regra-teto__opcao"
          :class="{ 'cashback-regra-teto__opcao--ativa': comportamentoTetoAtual === opcao.chave }"
        >
          <input
            type="radio"
            name="cashback-regra-teto"
            :value="opcao.chave"
            :checked="comportamentoTetoAtual === opcao.chave"
            @change="alterar('comportamentoTeto', opcao.chave)"
          />
          <span>
            <strong>{{ opcao.titulo }}</strong>
            <small>{{ opcao.descricao }}</small>
          </span>
        </label>
      </div>
    </div>

    <div class="cashback-regra-faixas__section" v-if="item.faixas.length">
      <div class="cashback-regra__secao-titulo">Simulação</div>
      <CRow class="align-items-end">
        <CCol md="4">
          <label class="cashback-regra__rotulo">Valor da base</label>
          <InputMoney :key="`simulacao-${remontagem}`" :value="valorSimulado" @input="definirSimulacao" />
        </CCol>
        <CCol md="8">
          <div class="cashback-regra-faixas__simulacao" v-if="simulacao">
            <span class="cashback-regra-faixas__simulacao-valor">{{ formatMoney(simulacao.valor) }}</span>
            <span class="cashback-regra-faixas__simulacao-texto">
              {{ simulacao.percentual > 0 ? `de cashback (${formatPercentual(simulacao.percentual)})` : "de cashback (faixa sem cashback)" }}
              <template v-if="simulacao.proxima">
                · faltam {{ formatMoney(simulacao.proxima.falta) }} para {{ formatPercentual(simulacao.proxima.percentual) }}
              </template>
            </span>
            <span v-if="simulacao.limitePedido !== null" class="cashback-regra-faixas__simulacao-limite">
              A faixa daria {{ formatMoney(simulacao.calculado) }}, limitado a {{ formatMoney(simulacao.limitePedido) }} por pedido.
            </span>
            <span v-if="limitesHistoricos" class="cashback-regra-faixas__simulacao-limite">
              {{ limitesHistoricos }}
            </span>
          </div>
          <div class="cashback-regra-faixas__simulacao cashback-regra-faixas__simulacao--vazio" v-else>
            {{ semFaixaTexto }}
          </div>
        </CCol>
      </CRow>
    </div>
  </div>
</template>

<script>
import InputMoney from "@/components/InputMoney";
import InputPercent from "@/components/InputPercent";
import { EXEMPLO_VALOR_PEDIDO, centavos, formatMoney, formatPercentual, numero, ordenarFaixas, positivo } from "../../helpers";
import { avisosFaixas, cascatearFaixas, errosFaixas, faixasAlteradas, novaFaixa, opcoesComportamentoTeto } from "../helpers";

export default {
  name: "StepFaixas",
  components: { InputMoney, InputPercent },

  props: {
    item: { type: Object, required: true },
    basesValor: { type: Array, default: () => [] },
    comportamentosTeto: { type: Array, default: () => [] },
  },

  data() {
    return {
      valorSimulado: EXEMPLO_VALOR_PEDIDO,
      remontagem: 0,
      versoes: {},
      campoAtivo: null,
      originais: null,
    };
  },

  computed: {
    avisos() {
      return avisosFaixas(this.item.faixas);
    },

    erros() {
      return errosFaixas(this.item.faixas);
    },

    faixasOrdenadas() {
      return ordenarFaixas(this.item.faixas);
    },

    semFaixaTexto() {
      const base = Number(this.valorSimulado || 0);
      const primeira = this.faixasOrdenadas[0];

      if (this.tetoUltimaFaixa !== null && base > this.tetoUltimaFaixa) return "Valor acima do teto da última faixa, não gera cashback.";

      return primeira && base >= Number(primeira.valorMinimo || 0)
        ? "Valor entre faixas, não gera cashback."
        : "Valor abaixo da primeira faixa, não gera cashback.";
    },

    tetoUltimaFaixa() {
      const ultima = this.faixasOrdenadas[this.faixasOrdenadas.length - 1];

      return ultima && Number(ultima.valorMaximo) > 0 ? Number(ultima.valorMaximo) : null;
    },

    limitaAoTeto() {
      return this.item.comportamentoTeto === "LIMITA_AO_TETO";
    },

    comportamentoTetoAtual() {
      return this.item.comportamentoTeto || "SEM_CASHBACK";
    },

    opcoesTeto() {
      return opcoesComportamentoTeto(this.faixasOrdenadas);
    },

    limitesHistoricos() {
      const limites = [
        positivo(this.item.limiteValorCliente) !== null ? "por cliente" : null,
        positivo(this.item.limiteValorTotal) !== null ? "total da regra" : null,
      ].filter(Boolean);

      if (!limites.length) return null;

      return limites.length > 1
        ? `Os limites ${limites.join(" e ")} dependem do cashback já gerado e não entram na simulação.`
        : `O limite ${limites[0]} depende do cashback já gerado e não entra na simulação.`;
    },

    simulacao() {
      const base = Number(this.valorSimulado || 0);
      let faixa = this.faixasOrdenadas.find((opcao) => {
        const minimo = Number(opcao.valorMinimo || 0);
        const maximo = Number(opcao.valorMaximo || 0);

        return base >= minimo && (!maximo || base <= maximo);
      });

      const acimaDoTeto = !faixa && this.limitaAoTeto && this.tetoUltimaFaixa !== null && base > this.tetoUltimaFaixa;

      if (acimaDoTeto) faixa = this.faixasOrdenadas[this.faixasOrdenadas.length - 1];

      if (!faixa) return null;

      const proxima = this.faixasOrdenadas.find(
        (opcao) => Number(opcao.valorMinimo || 0) > base && Number(opcao.percentual) > Number(faixa.percentual)
      );

      const calculado = Math.round((acimaDoTeto ? this.tetoUltimaFaixa : base) * Number(faixa.percentual)) / 100;
      const limitePedido = positivo(this.item.limiteValorPedido);
      const limitado = limitePedido !== null && calculado > limitePedido;

      return {
        percentual: Number(faixa.percentual),
        valor: limitado ? limitePedido : calculado,
        calculado,
        limitePedido: limitado ? limitePedido : null,
        proxima: proxima
          ? { falta: Number(proxima.valorMinimo) - base, percentual: Number(proxima.percentual) }
          : null,
      };
    },
  },

  methods: {
    formatMoney,
    formatPercentual,

    alterar(campo, valor) {
      this.$emit("alterar", { [campo]: valor });
    },

    valorValido(valor, anterior) {
      const convertido = Number(valor);

      if (Number.isNaN(convertido) || convertido < 0 || Object.is(convertido, -0)) {
        this.remontagem += 1;

        return Number(anterior) > 0 ? Number(anterior) : 0;
      }

      return convertido;
    },

    definirPercentual(indice, valor) {
      const atual = this.item.faixas[indice].percentual;
      const percentual = this.valorValido(valor, atual);

      if (atual !== null && atual !== "" && numero(atual) === percentual) return;

      this.alterar(
        "faixas",
        this.item.faixas.map((faixa, posicao) => (posicao === indice ? { ...faixa, percentual } : faixa))
      );
    },

    focar(indice, campo) {
      this.campoAtivo = `${indice}-${campo}`;
      this.originais = this.item.faixas.map((faixa) => ({ ...faixa }));
    },

    desfocar() {
      this.campoAtivo = null;
      this.originais = null;
    },

    definirValor(indice, campo, valor) {
      if (this.campoAtivo !== `${indice}-${campo}` || !this.originais) return;

      const atual = this.item.faixas[indice][campo];
      const novo = this.valorValido(valor, atual);

      if (centavos(novo) === centavos(atual)) return;

      const faixas = cascatearFaixas(this.originais, indice, campo, novo);

      faixasAlteradas(this.item.faixas, faixas)
        .filter((posicao) => posicao !== indice)
        .forEach((posicao) => this.$set(this.versoes, posicao, (this.versoes[posicao] || 0) + 1));

      this.alterar("faixas", faixas);
    },

    definirSimulacao(valor) {
      this.valorSimulado = this.valorValido(valor, this.valorSimulado);
    },

    adicionarFaixa() {
      this.alterar("faixas", [...this.item.faixas, novaFaixa(this.item.faixas)]);
    },

    removerFaixa(indice) {
      this.alterar(
        "faixas",
        this.item.faixas.filter((faixa, posicao) => posicao !== indice)
      );
    },
  },
};
</script>

<style scoped>
.cashback-regra-teto {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.cashback-regra-teto__opcao {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  cursor: pointer;
}

.cashback-regra-teto__opcao input {
  margin-top: 3px;
}

.cashback-regra-teto__opcao span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cashback-regra-teto__opcao small {
  color: #667085;
}

.cashback-regra-teto__opcao--ativa {
  border-color: var(--cashback-cor-primaria, #3a3e81);
  background-color: var(--cashback-cor-primaria-suave, #eceaf3);
}

.cashback-regra__aviso {
  margin-bottom: 12px;
  padding: 10px 14px;
  border: 1px solid #eaecf0;
  border-left: 3px solid var(--cashback-cor-primaria);
  border-radius: 8px;
  background-color: #f8fafc;
  font-size: 12.5px;
  color: #475467;
}

.cashback-regra-faixas__section {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}

.cashback-regra-faixas__section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.cashback-regra__secao-titulo {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.cashback-regra__rotulo {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.cashback-regra-faixas__base-dica {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #667085;
}

.cashback-regra-faixas__cabecalho-dica {
  font-weight: 400;
  text-transform: none;
  color: #98a2b3;
}

.cashback-regra-faixas__entrada--erro {
  border-color: #f04438;
  box-shadow: 0 0 0 2px rgba(240, 68, 56, 0.12);
}

.cashback-regra-faixas__aviso-linha {
  margin: -2px 0 8px;
  font-size: 11.5px;
  color: #b54708;
}

.cashback-regra-faixas__erros {
  display: grid;
  grid-template-columns: 1fr 1fr 160px 36px;
  gap: 8px;
  margin: -2px 0 8px;
  font-size: 11.5px;
  color: #d92d20;
}

.cashback-regra-faixas__cabecalho,
.cashback-regra-faixas__linha {
  display: grid;
  grid-template-columns: 1fr 1fr 160px 36px;
  gap: 8px;
  align-items: center;
}

.cashback-regra-faixas__cabecalho {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  margin: 12px 0 4px;
}

.cashback-regra-faixas__linha {
  margin-bottom: 6px;
}

.cashback-regra-faixas__remover {
  width: 32px;
  height: 32px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  background: #fff;
  color: #d92d20;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.cashback-regra-faixas__remover:hover {
  background-color: #fef3f2;
  border-color: #fda29b;
}

.cashback-regra-faixas__vazio {
  font-size: 13px;
  color: #aaa;
  font-style: italic;
  padding: 8px 0;
}

.cashback-regra-faixas__simulacao {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: #edf8f9;
  min-height: 38px;
}

.cashback-regra-faixas__simulacao-valor {
  font-size: 18px;
  font-weight: 700;
  color: #2298a3;
}

.cashback-regra-faixas__simulacao-texto {
  font-size: 12px;
  color: #475467;
}

.cashback-regra-faixas__simulacao-limite {
  display: block;
  width: 100%;
  font-size: 12px;
  color: #6c757d;
}

.cashback-regra-faixas__simulacao--vazio {
  background-color: #f9fafb;
  color: #999;
  font-size: 12px;
  align-items: center;
}
</style>
